import React, { useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import { CurrentRefinementsConnectorParamsRefinement } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import { useCurrentRefinements, useRange } from 'components/commercetools-ui/organisms/product/product-list-ssr/local-store';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import { useProductList } from '../../context';
import useRefinementHelpers from '../../hooks/useRefinementHelpers';

// Added ISNZCOMPLAINT
import {
  CAPACITY_SIZE,
  CAPACITY_TO_OVERFLOW,
  CLEAN_FLUSH,
  INDEPENDENT_LIVING_COMPLAINT,
  ISAMBULANT,
  ISCOMPLIANT,
  LITRES_PER_MINUTE,
  OUTLET_LENGTH,
  OVERFLOW,
  PAN_HEIGHT,
  PAN_PROJECTION,
  RAIL_LENGTH,
  SHOWER_HEAD_SIZE,
  ISNZCOMPLAINT,
  TEMPERATURE_ADJUSTABLE,
  FLOW_ADJUSTABLE,
} from 'helpers/constants/attributes';
import usePath from 'helpers/hooks/usePath';
import { groupRefinementsByBucket } from 'helpers/utils/measurementBuckets';
import { localeMapping } from 'helpers/constants/localeMapping';

const CurrentRefinements = () => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const { locale } = useParams();

  const { numericRanges, removeRefinement, pricesConfiguration } = useProductList();

  const { currency } = useI18n();
  const path = usePath();
  const localeKey = localeMapping[locale];

  const { items } = useCurrentRefinements();

  const { resolveLabel } = useRefinementHelpers();

  const [outletAttribute, setOutletAttribute] = useState('')
  
    const nonNumericRefinements = useMemo(
    () => items.map((item) => item.refinements.filter((refinement) => refinement.type !== 'numeric')).flat(),
    [items]
  );
  const configuration = useMemo(() => pricesConfiguration[outletAttribute], [pricesConfiguration, outletAttribute]);

  const numericRefinements = useMemo(() => {
    const refinements = items
      .map((item) => item.refinements.filter((refinement) => refinement.type === 'numeric'))
      .flat();
      setOutletAttribute(refinements?.[0]?.attribute)
    const refinementsMap = refinements.reduce<Record<string, [number, number]>>(
      (acc, curr) => ({
        ...acc,
        [curr.attribute]: [
          curr.operator?.includes('>') ? +curr.value : acc[curr.attribute]?.[0] ?? numericRanges[curr.attribute]?.[0],
          curr.operator?.includes('<') ? +curr.value : acc[curr.attribute]?.[1] ?? numericRanges[curr.attribute]?.[1],
        ],
      }),
      {}
    );

    const finalRefinements = Object.entries(refinementsMap).map<CurrentRefinementsConnectorParamsRefinement>(
      ([attribute, range]) =>
        ({
          type: 'numeric',
          attribute,
          label: `${(range[0] || 0) + 'mm' + ' -'} ${range[1] ? range[1] + 'mm' : configuration?.ranges?.[configuration?.ranges.length-1]?.max + 'mm'}`,
        } as CurrentRefinementsConnectorParamsRefinement)
    );
    return finalRefinements;
  }, [items, numericRanges, currency, locale]);

  if (!nonNumericRefinements.length && !numericRefinements.length) return <></>;


  //  Added "acc[attribute] = 'NZ4121 Accessible';"
  const typeMap = nonNumericRefinements.reduce<Record<string, string>>((acc, x) => {
    const attribute = x.attribute;
    const value = x.value;
    if (typeof attribute === 'string') {
      if (path.path.includes('brands') && attribute.includes(`categories.${localeKey}.lvl1`)) {
        acc[attribute] = 'Sub Category';
      } else if (path.path.includes('brands') && attribute.includes(`categories.${localeKey}.lvl0`)) {
        acc[attribute] = 'Category';
      } else if (path.path.includes('clark') && attribute.includes(`categories.${localeKey}.lvl1`)) {
        acc[attribute] = 'Sub Category';
      } else if (path.path.includes('clark') && attribute.includes(`categories.${localeKey}.lvl0`)) {
        acc[attribute] = 'Category';
      } else if (path.path.includes('dorf') && attribute.includes(`categories.${localeKey}.lvl1`)) {
        acc[attribute] = 'Sub Category';
      } else if (path.path.includes('dorf') && attribute.includes(`categories.${localeKey}.lvl0`)) {
        acc[attribute] = 'Category';
      } else if (attribute.includes('categories')) {
        acc[attribute] = 'Categories';
      } else if (attribute.includes('range')) {
        acc[attribute] = 'Collection';
      } else if (attribute.includes('colour')) {
        acc[attribute] = 'Colour';
      } else if (attribute.includes('brand')) {
        acc[attribute] = 'Brand';
      } else if (attribute.includes('coating-method')) {
        acc[attribute] = 'Coating Method';
      } else if (attribute.includes('temperature-adjustable')) {
        acc[attribute] = 'Temperature Adjustable';        
      } else if (attribute.includes('flow-adjustable')) {
        acc[attribute] = 'Flow Adjustable';        
      } else if (attribute.includes('shroud-available')) {
        acc[attribute] = 'Shroud Available';
      } else if (attribute.includes('pedestal-available')) {
        acc[attribute] = 'Pedestal Available';
      } else if (attribute.includes('product-type')) {
        acc[attribute] = typeof value === 'string' && value.includes("Grab Rails") ? 'Categories' : 'Product Type';
      } else if (attribute.includes('item-material')) {
        acc[attribute] = 'Material';
      } else if (attribute.includes('trap-type')) {
        acc[attribute] = 'Trap Type';
      } else if (attribute.includes('inlet-type')) {
        acc[attribute] = 'Inlet Type';
      } else if (attribute.includes('outlet-length')) {
        acc[attribute] = 'Outlet Length';
      } else if (attribute.includes('outlet-type')) {
        acc[attribute] = 'Outlet Type';
      } else if (attribute.includes('pan-projection')) {
        acc[attribute] = 'Pan Projection';
      } else if (attribute.includes('clean-flush')) {
        acc[attribute] = 'Cleanflush';
      } else if (attribute.includes('pan-height')) {
        acc[attribute] = 'Pan Height';
      } else if (attribute.includes('category')) {
        acc[attribute] = 'Category';
      } else if (attribute.includes("capacity-to-overflow")) {
        acc[attribute] = 'Capacity to Overflow';
      } else if (attribute.includes("capacity-size")) {
        acc[attribute] = 'Capacity Size';
      } else if (attribute.includes("shape")) {
        acc[attribute] = 'Shape';
      } else if (attribute.includes("tap-holes")) {
        acc[attribute] = 'Tapholes';
      } else if (attribute.includes(".wels-with-flow-rate-flush-volume-1")) {
        acc[attribute] = 'WELS Rating';
      } else if (attribute.includes("overflow")) {
        acc[attribute] = 'Overflow';
      } else if (attribute.includes("litres-per-minute")) {
        acc[attribute] = "Litres Per Minute";
      } else if (attribute.includes("IsAS14281Compliant")) {
        acc[attribute] = "AS1428.1 Accessible";
      } else if (attribute.includes("IsAS14281Ambulant")) {
        acc[attribute] = "AS1428.1 Ambulant";
      } else if (attribute.includes("IsIndependentLivingCompliant")) {
        acc[attribute] = "Independent Living Compliant";
      } else if (attribute.includes("cartridge-type")) {
        acc[attribute] = "Cartridge Type";
      } else if (attribute.includes("cartidge-size")) {
        acc[attribute] = "Cartridge Size";
      }  else if (attribute.includes("surface-finish")) {
        acc[attribute] = "Surface Finish";
      } else if (attribute.includes("waste-position")) {
        acc[attribute] = "Waste Position";
      } else if (attribute.includes("spray-function")) {
        acc[attribute] = "Spray Function";
      } else if (attribute.includes("rail-length")) {
        acc[attribute] = "Rail Length";
      } else if (attribute.includes("shower-head-diameter")) {
        acc[attribute] = "Shower Head Size";
      }  else if (attribute.includes("wels-with-flow-rate-flush-volume-2")) {
        acc[attribute] = "WELS Rating";
      }  else if (attribute.includes("mount-type")) {
        acc[attribute] = "Mount Type";
      } else if (attribute.includes("bowl-position" )) {
        acc[attribute] = "Bowl Position" ;
      } else if (attribute.includes('variants.attributes.IsNZ41121Compliant')) {
        acc[attribute] = 'NZ4121 Accessible';
      }
    }
    return acc;
  }, {}); 

  const numericTypeMap = numericRefinements.reduce<Record<string, string>>((acc, x) => {
    const attribute = x.attribute;
    if (typeof attribute === 'string') {
       if (attribute.includes('outlet-length')) {
        acc[attribute] = 'Outlet Length';
      } else if (attribute.includes('outlet-type')) {
        acc[attribute] = 'Outlet Type';
      } else if (attribute.includes('pan-projection')) {
        acc[attribute] = 'Pan Projection';
      } else if (attribute.includes('clean-flush')) {
        acc[attribute] = 'Cleanflush';
      } else if (attribute.includes('pan-height')) {
        acc[attribute] = 'Pan Height';
      }
    }
    return acc;
  }, {});

  const combinedTypeMap = { ...typeMap, ...numericTypeMap };

  // A bucket chip (e.g. "Pan Projection: 551-650mm") stands for several values — remove them all.
  const removeRefinements = (members: CurrentRefinementsConnectorParamsRefinement[]) => {
    members.forEach((m) => removeRefinement(m));
  };
  

  // Set Yes/No based on Filter selection for ISNZCOMPLAINT
  return (
    <div className="mt-10 flex flex-wrap items-center justify-start gap-8 px-24 md:px-28 lg:gap-14 lg:px-0 xl:px-0 2xl:px-0">
      {groupRefinementsByBucket([...numericRefinements, ...nonNumericRefinements]).map(({ refinement, members, bucketLabel }) => (
        <div
          key={bucketLabel ? `${refinement.attribute}-${bucketLabel}` : refinement.value}
          className="flex cursor-default items-center justify-center gap-8 rounded border border-neutral-500 bg-white px-8 py-6 transition hover:border-primary-black"
        >
        <span className="text-12 leading-[20px] text-secondary-black 2xl:text-14">
            {combinedTypeMap[refinement.attribute]}:  {bucketLabel
              ? bucketLabel
              : refinement.attribute.split('.').pop() === PAN_HEIGHT ||
            refinement.attribute.split('.').pop() === PAN_PROJECTION ||
            refinement.attribute.split('.').pop() === RAIL_LENGTH ||
            refinement.attribute.split('.').pop() === SHOWER_HEAD_SIZE
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop()}mm`
              : refinement.attribute.split('.').pop() === LITRES_PER_MINUTE
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop()}L/min`
              : refinement.attribute.split('.').pop() === CAPACITY_SIZE
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop()}L`
              : refinement.attribute.split('.').pop() === OVERFLOW
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === TEMPERATURE_ADJUSTABLE
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === FLOW_ADJUSTABLE
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === OVERFLOW
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === "shroud-available"
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === "pedestal-available"
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === CAPACITY_TO_OVERFLOW
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === CLEAN_FLUSH
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === "IsAS14281Compliant"
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === "IsAS14281Ambulant"
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === "IsIndependentLivingCompliant"
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : refinement.attribute.split('.').pop() === 'IsNZ41121Compliant'
              ? `${resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop() === 'true' ? 'Yes' : 'No'}`
              : resolveLabel(refinement.attribute, refinement.label)?.split('>')?.pop()}

          </span>
          
          <CloseIcon
            className="w-16 cursor-pointer fill-secondary-black stroke-0 2xl:w-20"
            onClick={() => removeRefinements(members)}
          />
        </div>
      ))}
    </div>
  );
};

export default CurrentRefinements;
